import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-black pt-32 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/10 border border-red-600/20 mb-6">
            <Shield size={14} className="text-red-600" />
            <span className="text-[10px] font-black tracking-[0.3em] uppercase text-red-600">Your Privacy Matters</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-black italic uppercase leading-[0.85] text-white tracking-tighter mb-6">
            PRIVACY <span className="text-gold">POLICY</span>
          </h1>

          <p className="text-zinc-400 text-lg">Last updated: May 12, 2026</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8 text-zinc-300"
        >
          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">1. Introduction</h2>
            <p className="leading-relaxed">
              Chaitanya Shooting Academy ("we," "us," "our," or "Company") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">2. Information We Collect</h2>
            <div className="space-y-4">
              <p>
                <strong className="text-white">Personal Information:</strong> Name, email address, phone number, age, gender, address provided during registration or booking.
              </p>
              <p>
                <strong className="text-white">Health Information:</strong> Medical history, any injuries, health conditions relevant to shooting training (with your consent).
              </p>
              <p>
                <strong className="text-white">Payment Information:</strong> Credit/debit card details processed securely through payment gateways. We do not store card details on our servers.
              </p>
              <p>
                <strong className="text-white">Device Information:</strong> Browser type, IP address, device type, operating system, pages visited, time spent on pages.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">3. How We Use Your Information</h2>
            <ul className="space-y-3 list-disc list-inside">
              <li>To process bookings and provide training services</li>
              <li>To send training schedules, updates, and notifications</li>
              <li>To process payments securely</li>
              <li>To improve our website and services</li>
              <li>To comply with legal obligations</li>
              <li>To send promotional offers (with your consent)</li>
              <li>For safety and security purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">4. Firebase & Data Storage</h2>
            <p className="leading-relaxed">
              We use Google Firebase for data storage and authentication. Your data is encrypted and stored on Google's secure servers located globally. Firebase complies with GDPR, CCPA, and international data protection standards.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">5. Data Sharing</h2>
            <p className="leading-relaxed mb-4">
              We do NOT share your personal information with third parties without your explicit consent, except:
            </p>
            <ul className="space-y-2 list-disc list-inside">
              <li>With service providers (payment gateways, hosting providers)</li>
              <li>When required by law or legal processes</li>
              <li>To protect safety and security</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">6. Cookies & Tracking</h2>
            <p className="leading-relaxed">
              We use cookies for essential functionality and analytics (Google Analytics). You can control cookie preferences in your browser settings. Essential cookies cannot be disabled as they are necessary for the website to function.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">7. Your Rights</h2>
            <p className="leading-relaxed mb-4">You have the right to:</p>
            <ul className="space-y-2 list-disc list-inside">
              <li>Access your personal data</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your data (right to be forgotten)</li>
              <li>Opt-out of marketing communications</li>
              <li>Download your data in portable format</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">8. Security</h2>
            <p className="leading-relaxed">
              We implement industry-standard security measures including SSL encryption, secure authentication, and access controls. However, no internet transmission is 100% secure. We encourage strong passwords and safe browsing practices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">9. Children's Privacy</h2>
            <p className="leading-relaxed">
              Our services are available to users of all ages. For minors under 18, we require parental consent for data collection. Parents can request access to or deletion of their child's data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">10. Contact Us</h2>
            <p className="leading-relaxed">
              For privacy concerns or to exercise your rights:
            </p>
            <div className="mt-4 p-6 bg-zinc-900/50 border border-white/10 rounded-2xl">
              <p className="mb-2"><strong className="text-white">Email:</strong> privacy@chaitanyashooting.com</p>
              <p className="mb-2"><strong className="text-white">Phone:</strong> +91 96535 24055</p>
              <p><strong className="text-white">Address:</strong> Sector 81, Gurugram, Haryana 122050</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-black text-white mb-4 uppercase">11. Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy periodically. We'll notify you of significant changes via email or prominent notice on our website. Your continued use constitutes acceptance of changes.
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
}
